import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AaiRequest, AaiSkill, CompanyDashboard, ControlEvent, DepartmentAggregate, ImpactData, OwnerAdoption } from '../../types/aai';
import {
  Action, Card, Chip, Divided, EmptyState, ErrorNote, Kicker, ListRow, Loading, NotMeasured, PageBody, PageHead, Panel,
  PanelHead, RouteMark, Stat,
} from '../../components/aa';
import { Tiles } from '../owner/ControlDrawers';
import { EvidenceRulesDrawer } from '../owner/AdoptionPage';
import { ownerOf } from '../partner/work';
import { relativeTime } from '../partner/format';
import { AssignBlockerDrawer, DemonstrationDrawer, PlanSupportDrawer } from './AdoptionDrawers';
import { ActionUpdateDrawer } from './ActionUpdateDrawer';
import { SharePanel } from './SharePanel';
import { ACTION_KINDS, ACTION_KIND_LABEL, isClosed, isOpenAction, latestOpenFor, sentTo, shortName, sideOf, statusOf, teamOf } from './actionStatus';
import { readinessLine } from './ActionUpdateDrawer';
import { bandLabel, isProtected, kindOf, linkedWorkFor, plural, teamsNeedingSupport } from './champion';

/*
 * AI Adoption, for the AI Champion: move approved Projects from readiness to
 * useful, sustained work. Every number is a company or team aggregate; groups
 * under the reporting threshold read as protected. Planning support records a
 * support request on the control record and names a team, a blocker and a
 * slot, never a person.
 */

const REPORTING_NOTE = 'Groups under the reporting threshold are protected.';

type TeamState = 'highest' | 'needs' | 'protected' | 'track';

const TEAM_CHIP: Record<TeamState, { label: string; tone: 'warn' | 'neutral' | 'ok' }> = {
  highest: { label: 'Highest priority', tone: 'warn' },
  needs: { label: 'Needs support', tone: 'warn' },
  protected: { label: 'Protected', tone: 'neutral' },
  track: { label: 'On track', tone: 'ok' },
};

const teamState = (d: DepartmentAggregate, priority: DepartmentAggregate[]): TeamState => {
  if (isProtected(d)) return 'protected';
  if (priority[0] && priority[0].department === d.department) return 'highest';
  if (priority.some((p) => p.department === d.department)) return 'needs';
  return 'track';
};

const ORDER: Record<TeamState, number> = { highest: 0, needs: 1, protected: 2, track: 3 };

const teamName = (d: DepartmentAggregate): string => {
  if (!d.department || d.department === '—') return 'Unassigned';
  return d.department;
};

const teamFoot = (d: DepartmentAggregate): string => {
  if (isProtected(d)) return d.suppressed_reason || 'Readiness protected under the reporting threshold';
  return `${bandLabel(d.maturity?.band)} · ${d.maturity?.maturity_pct ?? 0}% readiness · ${d.onboarded_count} onboarded`;
};

const linkedLine = (linked: AaiRequest | null): string => {
  if (!linked) return 'No linked Project yet';
  return `${linked.title} · ${kindOf(linked)}`;
};

const actionChip = (e: ControlEvent, me: string): React.ReactNode => {
  const state = statusOf(e.status);
  let who = `by ${shortName(e.actor_email)}`;
  if (sideOf(e, me) === 'raiser') who = 'by you';
  const respondedBy = (e.payload || {}).responded_by;
  if (typeof respondedBy === 'string' && respondedBy) who = `by ${shortName(respondedBy)}`;
  return <Chip tone={state.tone} dot>{ACTION_KIND_LABEL[e.kind] || 'Action'} · {state.label} · {who}</Chip>;
};

type Drawer = { kind: 'support'; team: string | null } | { kind: 'blocker' } | { kind: 'demo' } | { kind: 'update'; event: ControlEvent };

const DAY = 86400000;
const FOLLOW_UP_AFTER_DAYS = 14;

interface Snapshot { maturity_pct: number | null; need_support: number | null; onboarded: number; suppressed: boolean }

const snapshotOf = (value: unknown): Snapshot | null => {
  if (!value || typeof value !== 'object') return null;
  return value as Snapshot;
};

const closedAt = (e: ControlEvent): string | null => {
  const value = (e.payload || {}).closed_at;
  if (typeof value === 'string') return value;
  return null;
};

const daysSince = (iso: string | null): number | null => {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return Math.floor((Date.now() - at) / DAY);
};

/** What the team reads now, from the same dashboard the tiles use. */
const liveReadiness = (dash: CompanyDashboard, team: string | null): Snapshot | null => {
  if (!team) return null;
  const d = (dash.departments || []).find((x) => (x.department || '').toLowerCase() === team.toLowerCase());
  if (!d) return null;
  const suppressed = isProtected(d);
  let need: number | null = null;
  if (!suppressed && d.stage_distribution) need = d.stage_distribution.nascent || 0;
  let pct: number | null = null;
  if (!suppressed) pct = d.maturity?.maturity_pct ?? null;
  return { maturity_pct: pct, need_support: need, onboarded: d.onboarded_count, suppressed };
};

const changeLine = (before: Snapshot | null, now: Snapshot | null): string => {
  if (!before || !now) return 'Not measured';
  if (before.suppressed || now.suppressed || before.maturity_pct === null || now.maturity_pct === null) return 'Protected';
  const delta = now.maturity_pct - before.maturity_pct;
  if (delta > 0) return `+${delta} points`;
  if (delta < 0) return `${delta} points`;
  return 'No change yet';
};

const ChampionAdoptionPage: React.FC = () => {
  const dashboard = useAsync<CompanyDashboard>(() => aaiApi.getDashboard(), 'Could not load company readiness.');
  const impact = useAsync<ImpactData>(() => aaiApi.getImpact(), 'Could not load adoption evidence.');
  const adoption = useAsync<OwnerAdoption>(() => aaiApi.getOwnerAdoption(), 'Could not load the adoption program.');
  const requests = useAsync<{ scope: string; requests: AaiRequest[] }>(() => aaiApi.listRequests(), 'Could not load company work.');
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load Quick Wins.');
  const support = useAsync<{ events: ControlEvent[] }>(() => aaiApi.listControlEvents(), 'Could not load adoption actions.');
  const [drawer, setDrawer] = useState<Drawer | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const me = localStorage.getItem('user_email') || '';

  if (dashboard.loading) return <PageBody><Loading label="Loading AI adoption" /></PageBody>;
  if (dashboard.error || !dashboard.data) return <PageBody><ErrorNote message={dashboard.error || 'Something went wrong.'} /></PageBody>;

  const dash = dashboard.data;
  const all = requests.data?.requests || [];
  const teams = teamsNeedingSupport(dash);
  const top = teams[0] || null;
  const events = support.data?.events || [];
  const teamNames = (dash.departments || []).map(teamName).filter((n) => n !== 'Unassigned');
  const priorityNames = teams.map((t) => t.department);
  const quickWins = (skills.data?.skills || []).filter((s) => s.published);
  const departments = [...(dash.departments || [])].sort((a, b) => {
    const diff = ORDER[teamState(a, teams)] - ORDER[teamState(b, teams)];
    if (diff !== 0) return diff;
    return (a.maturity?.maturity_pct ?? 0) - (b.maturity?.maturity_pct ?? 0);
  });

  const companySuppressed = dash.company.suppressed || Boolean(dash.company.maturity?.suppressed);
  const used = impact.data?.skills.shared_performance.adoption ?? null;
  let usedValue: React.ReactNode = <NotMeasured reason="No eligible people yet" />;
  if (used !== null && dash.enrolled > 0) usedValue = `${Math.round((used / dash.enrolled) * 100)}%`;
  let readinessValue: React.ReactNode = <NotMeasured reason="Protected" />;
  if (!companySuppressed && dash.company.maturity?.maturity_pct !== null && dash.company.maturity?.maturity_pct !== undefined) readinessValue = `${dash.company.maturity.maturity_pct}%`;
  let supportTone: 'attention' | 'default' = 'default';
  if (teams.length > 0) supportTone = 'attention';

  let managers: string[] = [];
  let managerLabel = 'Program manager';
  if (adoption.data) {
    managers = adoption.data.managers;
    if (adoption.data.program) managerLabel = adoption.data.program.manager_label;
  }

  const topLinked = top ? linkedWorkFor(top.department, all) : null;
  const topSupport = top ? latestOpenFor(events, top.department) : null;
  const openActions = events.filter(isOpenAction);
  const closedActions = events.filter((e) => ACTION_KINDS.includes(e.kind) && isClosed(e));
  const recentlyClosed = closedActions
    .filter((e) => (e.kind === 'support_request' || e.kind === 'demonstration') && e.status !== 'cancelled' && e.status !== 'declined')
    .filter((e) => {
      const days = daysSince(closedAt(e) || e.created_at);
      return days !== null && days <= 60;
    });
  let topTitle = 'No team needs support before first use';
  let topDesc = REPORTING_NOTE;
  let topChip: React.ReactNode = <Chip>Nothing waiting</Chip>;
  if (top) {
    topTitle = `${top.department} needs support before first use`;
    topDesc = `${linkedLine(topLinked)} · ${bandLabel(top.maturity?.band)} readiness`;
    topChip = <Chip tone="run">Action needed</Chip>;
  }
  if (topSupport) topChip = actionChip(topSupport, me);
  let topOwner = 'Not yet named';
  if (topLinked) topOwner = ownerOf(topLinked);

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow="Enterprise AI Office"
        title="AI Adoption"
        sub="Move approved Projects from readiness to useful, sustained work."
      />

      <div className="mb-5 grid gap-3 md:grid-cols-3">
        <Stat label="Used in work" value={usedValue} evidence="Eligible people using a shared capability" />
        <Stat label="Readiness" value={readinessValue} evidence="Company maturity · protected aggregate" />
        <Stat tone={supportTone} label="Need support" value={`${teams.length}`} evidence="Teams" />
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-[2.6fr_1fr]">
        <Panel>
          <div className="px-5 pb-6 pt-5 md:px-6 md:pt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Kicker className="mb-2">Highest priority</Kicker>
                <h2 className="m-0 text-[24px] font-bold leading-[30px] tracking-[-0.005em] text-ink">{topTitle}</h2>
                <p className="m-0 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">{topDesc}</p>
              </div>
              {topChip}
            </div>
            <div className="mt-5">
              <Tiles
                cols={2}
                facts={[
                  { label: 'Linked Project', value: linkedLine(topLinked) },
                  { label: 'Owner', value: topOwner },
                ]}
              />
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Action disabled={teamNames.length === 0} reason="No team is recorded yet" onClick={() => setDrawer({ kind: 'support', team: top?.department || null })}>Plan support</Action>
              <Action variant="quiet" onClick={() => setDrawer({ kind: 'blocker' })}>Assign shared blocker</Action>
              <Action variant="quiet" disabled={quickWins.length === 0} reason="No published Quick Win to demonstrate" onClick={() => setDrawer({ kind: 'demo' })}>Schedule demonstration</Action>
              <Action variant="quiet" onClick={() => setRulesOpen(true)}>How adoption evidence works</Action>
            </div>
          </div>
        </Panel>

        <Card pad="lg">
          <div className="flex items-start gap-3">
            <ShieldCheck size={22} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink" />
            <div>
              <Kicker className="mb-2">Your view</Kicker>
              <div className="text-[18px] font-bold leading-[24px] text-ink">AI Champion</div>
              <p className="m-0 mt-2 text-[12.5px] leading-[19px] text-ink-muted">
                Aggregate evidence only. Adoption actions do not approve data, scope, spend, or release.
              </p>
              <p className="m-0 mt-3 text-[12.5px] leading-[19px] text-ink-muted">{REPORTING_NOTE} Support requests name a team, a blocker and a slot, never a person.</p>
            </div>
          </div>
        </Card>
      </div>

      <Panel>
        <PanelHead kicker="Current work" title="Priority teams" action={<span className="text-[12px] text-ink-soft">{plural(departments.length, 'item', 'items')}</span>} />
        {departments.length === 0 && (
          <EmptyState title="No teams recorded yet" reason="Teams appear here once people are placed in departments and onboarded." />
        )}
        <Divided>
          {departments.map((d) => {
            const state = teamState(d, teams);
            const chip = TEAM_CHIP[state];
            const linked = linkedWorkFor(d.department, all);
            const latest = latestOpenFor(events, d.department);
            let mark: 'adoption' | 'project' = 'adoption';
            if (linked && kindOf(linked) === 'Delivery Project') mark = 'project';
            let right: React.ReactNode = <Chip tone={chip.tone}>{chip.label}</Chip>;
            if (latest) right = actionChip(latest, me);
            return (
              <ListRow
                key={d.department}
                mark={<RouteMark kind={mark} size="lg" />}
                title={teamName(d)}
                desc={linkedLine(linked)}
                foot={teamFoot(d)}
                right={(
                  <>
                    {right}
                    <Action size="sm" variant="quiet" disabled={teamName(d) === 'Unassigned'} reason="No department recorded" onClick={() => setDrawer({ kind: 'support', team: d.department })}>Plan support</Action>
                  </>
                )}
              />
            );
          })}
        </Divided>
      </Panel>

      <SharePanel className="mt-5" />

      <Panel className="mt-5">
        <PanelHead
          kicker="Response loop"
          title="Open actions"
          desc="What you have asked of teams, the Owner or the platform, and where each one stands. The other side answers; you close."
          action={<span className="text-[12px] text-ink-soft">{plural(openActions.length, 'open', 'open')}</span>}
        />
        {openActions.length === 0 && <EmptyState title="Nothing open" reason="Plan support, assign a blocker or schedule a demonstration and it appears here until it is closed." />}
        <Divided>
          {openActions.map((e) => {
            const state = statusOf(e.status);
            let recipients = 'Nobody, record only';
            if (sentTo(e).length > 0) recipients = sentTo(e).map(shortName).join(', ');
            let responded = 'Waiting';
            const respondedBy = (e.payload || {}).responded_by;
            if (typeof respondedBy === 'string' && respondedBy) responded = `${state.label} by ${shortName(respondedBy)}`;
            let bookedFoot = relativeTime(e.created_at);
            if ((e.payload || {}).session_id) bookedFoot = `On the calendar · ${String((e.payload || {}).invited || 0)} invited`;
            let mark: 'adoption' | 'quick_win' | 'connection' = 'adoption';
            if (e.kind === 'demonstration' || e.kind === 'capability_feedback' || e.kind === 'customization_request') mark = 'quick_win';
            if (e.kind === 'blocker_assignment') mark = 'connection';
            let label = 'Update';
            if (sideOf(e, me) === 'recipient') label = 'Respond';
            return (
              <ListRow
                key={e.id}
                mark={<RouteMark kind={mark} size="lg" />}
                title={e.subject}
                badge={<Chip tone={state.tone} dot>{ACTION_KIND_LABEL[e.kind] || e.kind} · {state.label}</Chip>}
                meta={[
                  { label: 'Team', value: teamOf(e) || 'Whole company' },
                  { label: 'Sent to', value: recipients },
                  { label: 'Response', value: responded, foot: bookedFoot },
                ]}
                right={<Action size="sm" variant="quiet" arrow onClick={() => setDrawer({ kind: 'update', event: e })}>{label}</Action>}
              />
            );
          })}
        </Divided>
      </Panel>

      <Panel className="mt-5">
        <PanelHead
          kicker="Effect"
          title="Delivered, and what the team reads now"
          desc="Readiness when the support was raised, next to readiness today. A closed action records the effort; only re-measured readiness shows the effect. After two weeks with no movement the row asks for a follow-up."
          action={<span className="text-[12px] text-ink-soft">{plural(recentlyClosed.length, 'closed in 60 days', 'closed in 60 days')}</span>}
        />
        {recentlyClosed.length === 0 && <EmptyState title="Nothing delivered yet" reason="Once support or a demonstration is marked delivered or held, its before and after readiness appears here." />}
        <Divided>
          {recentlyClosed.map((e) => {
            const before = snapshotOf((e.payload || {}).readiness_at_raise);
            const now = liveReadiness(dash, teamOf(e));
            const days = daysSince(closedAt(e) || e.created_at) || 0;
            const change = changeLine(before, now);
            let chip: React.ReactNode = <Chip tone="ok">{statusOf(e.status).label}</Chip>;
            if (days >= FOLLOW_UP_AFTER_DAYS && (change === 'No change yet' || change.startsWith('-'))) chip = <Chip tone="warn" dot>Follow up</Chip>;
            let beforeLine = 'Not recorded';
            if (before) beforeLine = readinessLine(before);
            let nowLine = 'Not measured';
            if (now) nowLine = readinessLine(now);
            let mark: 'adoption' | 'quick_win' = 'adoption';
            if (e.kind === 'demonstration') mark = 'quick_win';
            return (
              <ListRow
                key={e.id}
                mark={<RouteMark kind={mark} size="lg" />}
                title={e.subject}
                badge={chip}
                foot={`${ACTION_KIND_LABEL[e.kind]} · ${teamOf(e) || 'Whole company'} · closed ${relativeTime(closedAt(e) || e.created_at)}`}
                meta={[
                  { label: 'Then', value: beforeLine },
                  { label: 'Now', value: nowLine },
                  { label: 'Change', value: change, foot: `${days} days since close` },
                ]}
                right={<Action size="sm" variant="quiet" onClick={() => setDrawer({ kind: 'update', event: e })}>Open</Action>}
              />
            );
          })}
        </Divided>
      </Panel>

      <Panel className="mt-5">
        <PanelHead
          kicker="History"
          title="Closed actions"
          desc="Everything that has been delivered, held, resolved, declined, returned, cancelled or withdrawn. Nothing moves on a closed action; open one to read how it ended."
          action={<span className="text-[12px] text-ink-soft">{plural(closedActions.length, 'closed', 'closed')}</span>}
        />
        {closedActions.length === 0 && <EmptyState title="Nothing closed yet" reason="Closed actions stay here as the record of what was asked and how it ended." />}
        <Divided>
          {closedActions.slice(0, 20).map((e) => {
            const state = statusOf(e.status);
            let by = 'not recorded';
            const respondedBy = (e.payload || {}).responded_by;
            if (typeof respondedBy === 'string' && respondedBy) by = shortName(respondedBy);
            let mark: 'adoption' | 'quick_win' | 'connection' = 'adoption';
            if (e.kind === 'demonstration') mark = 'quick_win';
            if (e.kind === 'blocker_assignment') mark = 'connection';
            let recipients = 'Nobody, record only';
            if (sentTo(e).length > 0) recipients = sentTo(e).map(shortName).join(', ');
            return (
              <ListRow
                key={e.id}
                mark={<RouteMark kind={mark} size="lg" />}
                title={e.subject}
                badge={<Chip tone={state.tone} dot>{ACTION_KIND_LABEL[e.kind] || e.kind} · {state.label}</Chip>}
                meta={[
                  { label: 'Team', value: teamOf(e) || 'Whole company' },
                  { label: 'Sent to', value: recipients },
                  { label: 'Closed', value: `${state.label} by ${by}`, foot: relativeTime(closedAt(e) || String((e.payload || {}).responded_at || e.created_at)) },
                ]}
                right={<Action size="sm" variant="quiet" onClick={() => setDrawer({ kind: 'update', event: e })}>Open</Action>}
              />
            );
          })}
        </Divided>
      </Panel>

      {drawer?.kind === 'update' && <ActionUpdateDrawer event={drawer.event} onUpdated={support.reload} onClose={() => setDrawer(null)} />}
      {drawer?.kind === 'support' && (
        <PlanSupportDrawer teams={teamNames} priority={priorityNames} initialTeam={drawer.team} onRecorded={support.reload} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'blocker' && <AssignBlockerDrawer events={events} onRecorded={support.reload} onClose={() => setDrawer(null)} />}
      {drawer?.kind === 'demo' && <DemonstrationDrawer skills={quickWins} teams={teamNames} onRecorded={support.reload} onClose={() => setDrawer(null)} />}
      {rulesOpen && <EvidenceRulesDrawer managers={managers} managerLabel={managerLabel} onClose={() => setRulesOpen(false)} />}
    </PageBody>
  );
};

export default ChampionAdoptionPage;
